import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { track } from '../onboarding/track';
import { promptInstall, useCanPromptInstall } from './platform';

const DISMISSED_KEY = 'toiyp:install_card_dismissed';
/** A "not now" holds for a fortnight, then the card may come back. */
const QUIET_MS = 14 * 24 * 60 * 60 * 1000;

function dismissedRecently(): boolean {
  try {
    return Date.now() - Number(localStorage.getItem(DISMISSED_KEY) ?? 0) < QUIET_MS;
  } catch {
    return true;
  }
}

/**
 * "Install toiyp" for browsers that offer it (Chrome and Edge, on Android and
 * desktop). Shown on the letter list only while the browser is holding an
 * install prompt, so it never appears where it could not work. iPhone has no
 * such prompt; it gets the Home Screen steps from the notification ask.
 */
export function InstallCard() {
  const can = useCanPromptInstall();
  const [hidden, setHidden] = useState(dismissedRecently);
  const visible = can && !hidden;

  useEffect(() => {
    if (visible) track({ event: 'install_prompt_shown' });
  }, [visible]);

  if (!visible) return null;

  const install = async () => {
    const outcome = await promptInstall();
    track({ event: 'install_prompt_result', outcome });
    setHidden(true);
  };

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      // the card simply returns next visit
    }
    track({ event: 'install_prompt_result', outcome: 'not_now' });
    setHidden(true);
  };

  return (
    <div className="install-card" role="region" aria-label="Install toiyp">
      <Download size={16} aria-hidden="true" className="install-card__icon" />
      <span className="install-card__text">
        <strong>Install toiyp</strong>
        <span>Open it like an app, and hear when a letter arrives.</span>
      </span>
      <button type="button" className="btn btn--primary install-card__cta" onClick={() => void install()}>
        Install
      </button>
      <button type="button" className="install-card__close" onClick={dismiss} aria-label="Not now">
        <X size={15} aria-hidden="true" />
      </button>
    </div>
  );
}
